<script setup lang="ts">
import { nextTick, onBeforeUnmount, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import AppSidebarPanel from './AppSidebarPanel.vue'
import AppScrim from '../../primitives/AppScrim/AppScrim.vue'
import { useSidebar } from './useSidebar'
import { useScrollLock } from '../../../composables/useScrollLock'
import type { NavBadge, NavBrand, NavUser } from './nav-types'
import type { OnboardingChainStepView } from '@/types/onboarding-chain'

withDefaults(
  defineProps<{
    user?: NavUser | null
    brand?: NavBrand | null
    navBadges?: Record<string, NavBadge>
    /** Setup mode: the chain's steps take the place of the create button, the nav and the chats. */
    setup?: boolean
    /** The steps to list in setup mode, in chain order. */
    setupSteps?: readonly OnboardingChainStepView[]
    /** Turns on while setup hands over to Home, which lifts the setup steps away instead of dropping them. */
    handover?: boolean
    /** The theme on screen now; the switch at the foot of the nav shows it. */
    theme?: 'light' | 'dark'
  }>(),
  { user: null, brand: null, setup: false, setupSteps: () => [], handover: false, theme: 'light' },
)

const emit = defineEmits<{
  'sign-out': []
  'update:theme': [value: 'light' | 'dark']
}>()

const { openMobile, setOpenMobile } = useSidebar()
const { t } = useI18n()
const panelRef = ref<HTMLElement | null>(null)
const scrollLock = useScrollLock()
let lastFocused: HTMLElement | null = null

function close() {
  setOpenMobile(false)
}

function focusables(): HTMLElement[] {
  if (!panelRef.value) return []
  return Array.from(
    panelRef.value.querySelectorAll<HTMLElement>(
      'a[href], area[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [role="button"], [tabindex]:not([tabindex="-1"])',
    ),
  ).filter((element) => element.offsetParent !== null)
}

function onKeydown(event: KeyboardEvent) {
  if (event.key === 'Escape') {
    event.preventDefault()
    close()
    return
  }
  if (event.key !== 'Tab') return
  const elements = focusables()
  if (elements.length === 0) {
    event.preventDefault()
    panelRef.value?.focus()
    return
  }
  const first = elements[0]!
  const last = elements[elements.length - 1]!
  const active = document.activeElement as HTMLElement | null
  if (!panelRef.value?.contains(active)) {
    event.preventDefault()
    first.focus()
    return
  }
  if (event.shiftKey && active === first) {
    event.preventDefault()
    last.focus()
  } else if (!event.shiftKey && active === last) {
    event.preventDefault()
    first.focus()
  }
}

watch(openMobile, async (open) => {
  if (typeof document === 'undefined') return
  if (open) {
    lastFocused = document.activeElement as HTMLElement | null
    document.addEventListener('keydown', onKeydown)
    scrollLock.lock()
    await nextTick()
    ;(focusables()[0] ?? panelRef.value)?.focus()
  } else {
    document.removeEventListener('keydown', onKeydown)
    scrollLock.unlock()
    lastFocused?.focus()
    lastFocused = null
  }
})

onBeforeUnmount(() => {
  if (typeof document === 'undefined') return
  document.removeEventListener('keydown', onKeydown)
  if (openMobile.value) scrollLock.unlock()
})
</script>

<template>
  <Teleport to="body">
    <AppScrim :open="openMobile" dismissible @close="close" />
    <Transition
      enter-active-class="transition-transform duration-slower ease-soft"
      leave-active-class="transition-transform duration-slow ease-soft-exit"
      enter-from-class="-translate-x-full"
      leave-to-class="-translate-x-full"
    >
      <div
        v-if="openMobile"
        ref="panelRef"
        role="dialog"
        aria-modal="true"
        :aria-label="t('shell.sidebar')"
        tabindex="-1"
        class="fixed inset-y-0 left-0 z-50 w-(--sidebar-width-mobile) shadow-3 outline-none"
      >
        <AppSidebarPanel
          :user="user"
          :brand="brand"
          :nav-badges="navBadges"
          :setup="setup"
          :setup-steps="setupSteps"
          :handover="handover"
          :theme="theme"
          @sign-out="emit('sign-out')"
          @update:theme="emit('update:theme', $event)"
        >
          <template #nav-footer><slot name="nav-footer" /></template>
        </AppSidebarPanel>
      </div>
    </Transition>
  </Teleport>
</template>
